import React, { useMemo } from 'react';
import { useLocale } from '../../context/LocaleContext';
import { startOfWeek, addDays, format, isToday } from 'date-fns';
import useNames from '../../hooks/useNames';
import StatusGlyph from './StatusGlyph';
import { categoryPaint } from './eventPaint';
import s from './WeekListView.module.css';

// Week · List (SPEC.md §2, captured at ?view=WeekList).
//
// The same seven day columns as the Agenda view, with the time axis removed.
// Events stack in chronological order inside their day rather than being
// positioned against the clock.
//
// The two modes answer different questions, which is why MMS ships both.
// Agenda answers "where does this sit in my day"; List answers "what is on
// this week" without scrolling to 4pm to find a single lesson. Agenda is the
// default.
//
// The event's colour is its category's, and its attendance is the dot beside
// the name, as on MonthView (`categoryPaint` in eventPaint.js, #109, #130): a
// 3px left edge in the category's colour, or `--accent` for an event with no
// category. The dot is 6px here against the month's 7, because it sits inline
// with a name rather than leading a single line of text.
//
// While a series ticks "Show public description directly on calendar", MMS's
// Week List, in its default Detailed event view, writes the description as a
// block of its own under the category, below a thin divider, in small regular
// text in the event's own ink, wrapping (#796). `calendarDescription` is the
// core's answer to whether there is one (`calendar_description` in
// core/src/schedule.rs).

const CHIP = {
  plain: s.event,
  tinted: s.eventTinted,
};

export default function WeekListView({
  weekStart, occurrences, studentsById, categoryColorFor, onEventClick,
}) {
  // The Region's words and clock (#534): `Mo`, `Okt 1`, `16:00 - 16:30` in
  // Germany, as MMS writes its own.
  const { formatTime, weekStartsOn, formatWeekdayShort, formatMonthShort } = useLocale();
  const names = useNames();
  const days = useMemo(
    // From the day the studio's Region begins a week on (#505), not date-fns'
    // default Sunday: `weekStart` from the page already is that day, and any
    // other day in the week is taken back to it.
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(weekStart, { weekStartsOn }), i)),
    [weekStart, weekStartsOn]
  );

  const byDate = useMemo(() => {
    const m = new Map();
    for (const o of occurrences) {
      if (!m.has(o.date)) m.set(o.date, []);
      m.get(o.date).push(o);
    }
    // Chronological within the day — the only cue left once the time axis is
    // gone. All-day events sort to the top of their day, where a band above
    // the grid would put them.
    for (const list of m.values()) {
      list.sort((a, b) => {
        if (Boolean(a.allDay) !== Boolean(b.allDay)) return a.allDay ? -1 : 1;
        return a.startTime < b.startTime ? -1 : 1;
      });
    }
    return m;
  }, [occurrences]);

  // The clock the studio's locale uses: a de-DE or ja-JP studio writes 16:00.
  const timeLabel = (o) => {
    // An all-day event has no range to print (#160): "All Day" goes where the
    // clock would, as MMS's Month does. What MMS's Week List does is unchecked.
    if (o.allDay) return 'All Day';
    const [h, min] = String(o.startTime || '00:00').split(':').map(Number);
    const total = h * 60 + min + (o.durationMin || 0);
    const hhmm = (mins) => `${String(Math.floor(mins / 60) % 24).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
    return `${formatTime(o.startTime)} - ${formatTime(hhmm(total))}`;
  };

  return (
    <>

      <div className={s.week}>
        <div className={s.head}>
          {days.map((d) => (
            <div className={s.dow} key={d.toISOString()}>{formatWeekdayShort(format(d, 'yyyy-MM-dd'))}</div>
          ))}
        </div>
        <div className={s.body}>
          {days.map((d) => {
            const iso = format(d, 'yyyy-MM-dd');
            const list = byDate.get(iso) || [];
            const today = isToday(d);
            return (
              <div className={today ? s.colToday : s.col} key={iso}>
                {/* Drawn only on a phone, where the days stack and the header
                    row above them is hidden (#636). */}
                <div className={s.colDow}>{formatWeekdayShort(iso)}</div>
                <div className={today ? s.dayNumToday : s.dayNum}>
                  {format(d, 'd') === '1' ? `${formatMonthShort(iso)} 1` : format(d, 'd')}
                </div>
                {list.map((o) => {
                  const attendees = o.studentIds
                    .map((sid) => studentsById?.(sid))
                    .filter(Boolean)
                    .map((s) => names.student(s))
                    .join('; ');
                  return (
                    <button
                      key={o.id}
                      {...categoryPaint(o, categoryColorFor, CHIP)}
                      onClick={(e) => onEventClick?.(o, e.currentTarget)}
                    >
                      <div className={s.time}>{timeLabel(o)}</div>
                      <div className={s.who}>
                        <StatusGlyph status={o.status || 'unrecorded'} size={6} />
                        {attendees || o.categoryLabel}
                      </div>
                      <div className={s.cat}>{o.categoryLabel}</div>
                      {o.calendarDescription ? <div className={s.description}>{o.calendarDescription}</div> : null}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
